import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES, QUERY_KEYS } from '@/config/constants';
import { useAppStore } from '@/stores/app-store';
import { toaster } from '@/components/ui/lib/toast-utils';
import { DICTIONARY } from '@/config/dictionary';
import { getApiErrorMessage } from '@/utils/api-error';
import type { ServiceFeatureCatalog } from '@/types/service-feature-catalog';

const FALLBACK_WHOLESALER_SCOPE = 'default';

const getWholesalerScope = (
  selectedWholesalerId: string | null,
  firstWholesalerId: string | undefined,
): string => {
  return selectedWholesalerId ?? firstWholesalerId ?? FALLBACK_WHOLESALER_SCOPE;
};

const getWritableWholesalerScope = (
  selectedWholesalerId: string | null,
  firstWholesalerId: string | undefined,
): string => {
  const scope = selectedWholesalerId ?? firstWholesalerId;
  if (scope == null) {
    throw new Error('Cannot update catalog without a wholesaler scope');
  }
  return scope;
};

const fetchServiceFeatureCatalog = async (
  wholesalerScope: string,
): Promise<ServiceFeatureCatalog> => {
  const { data } = await api.get<ServiceFeatureCatalog>(API_ROUTES.services.catalog, {
    headers: {
      'x-wholesaler-id': wholesalerScope,
    },
  });
  return data;
};

export const getServiceFeatureCatalogQueryOptions = (wholesalerScope: string) =>
  queryOptions({
    queryKey: QUERY_KEYS.services.catalog(wholesalerScope),
    queryFn: () => fetchServiceFeatureCatalog(wholesalerScope),
  });

export const useServiceFeatureCatalog = () => {
  const selectedWholesalerId = useAppStore((state) => state.selectedWholesalerId);
  const firstWholesalerId = useAppStore((state) => state.currentUser?.wholesalers[0]?.id);
  const wholesalerScope = getWholesalerScope(selectedWholesalerId, firstWholesalerId);
  return useQuery(getServiceFeatureCatalogQueryOptions(wholesalerScope));
};

export const useDefaultServiceFeatureCatalog = () => {
  return useQuery(getServiceFeatureCatalogQueryOptions(FALLBACK_WHOLESALER_SCOPE));
};

const updateServiceFeatureCatalog = async (
  wholesalerScope: string,
  payload: ServiceFeatureCatalog,
): Promise<ServiceFeatureCatalog> => {
  const { data } = await api.put<ServiceFeatureCatalog>(API_ROUTES.services.catalog, payload, {
    headers: {
      'x-wholesaler-id': wholesalerScope,
    },
  });
  return data;
};

export const useUpdateServiceFeatureCatalog = () => {
  const queryClient = useQueryClient();
  const selectedWholesalerId = useAppStore((state) => state.selectedWholesalerId);
  const firstWholesalerId = useAppStore((state) => state.currentUser?.wholesalers[0]?.id);
  const wholesalerScope = getWritableWholesalerScope(selectedWholesalerId, firstWholesalerId);

  return useMutation({
    mutationFn: (payload: ServiceFeatureCatalog) =>
      updateServiceFeatureCatalog(wholesalerScope, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS.services.catalog(wholesalerScope), data);
      toaster.create({
        type: 'success',
        title: DICTIONARY.adminSettingsPage.toast.catalogSaved,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.adminSettingsPage.toast.catalogError,
        description: getApiErrorMessage(error),
      });
    },
  });
};
